import { parseDataAttributes } from '../../utilities/utilities'
import type { EnabledFilter } from '@haus-storefront-react/shared-types'

const VALID_FILTER_DISPLAY_TYPES = ['dropdown', 'accordion']

export type FiltersAttributes<T extends EnabledFilter = EnabledFilter> = {
  productListIdentifier: string
  enabledFilters: T[] | undefined
  displayType?: string
}

const getFilterDisplayType = (type: unknown): string => {
  if (typeof type !== 'string') {
    return 'dropdown'
  }

  if (!VALID_FILTER_DISPLAY_TYPES.includes(type)) {
    return 'dropdown'
  }

  return type
}

export function getFiltersAttributes(attrs: NamedNodeMap): FiltersAttributes {
  const base = parseDataAttributes(attrs)

  const priceFilterEnabled = Boolean(base.priceFilterEnabled)

  let rawFilters = base.filterValues as
    | Array<{
        filter_value: string
        filter_condition: 'AND' | 'OR' | 'NOT'
        filter_label?: string
      }>
    | string
    | undefined

  if (typeof rawFilters === 'string') {
    let parsed: unknown = undefined
    try {
      parsed = JSON.parse(rawFilters)
    } catch {
      try {
        const textArea = document.createElement('textarea')
        textArea.innerHTML = rawFilters
        const decoded = textArea.value.replace(/&quot;/g, '"')
        parsed = JSON.parse(decoded)
      } catch {
        parsed = undefined
      }
    }
    rawFilters = Array.isArray(parsed)
      ? (parsed as Array<{
          filter_value: string
          filter_condition: 'AND' | 'OR' | 'NOT'
          filter_label?: string
        }>)
      : undefined
  }

  const enabledFilters: EnabledFilter[] = Array.isArray(rawFilters)
    ? rawFilters.map((f) => ({
        facetCode: f.filter_value,
        logicalOperator: f.filter_condition.toLowerCase() as 'and' | 'or',
        label: f.filter_label || undefined,
        type: 'facet',
      }))
    : []

  if (priceFilterEnabled) {
    enabledFilters.push({ type: 'price' })
  }

  const displayType = getFilterDisplayType(base.displayType)

  return {
    productListIdentifier: base.productListIdentifier as string,
    enabledFilters,
    displayType,
  }
}

export default function propsFn(dataAttributes: NamedNodeMap): FiltersAttributes {
  return getFiltersAttributes(dataAttributes)
}
